import { useEffect, useRef, useState, useContext, useMemo } from "react";

//Components
import SingleDropDown from "./SingleDropDown";
import { FormControl, TextField, CircularProgress } from "@material-ui/core";
//Contexts
import { SetupContext } from "../Contexts/SetupContext";
import { INCIDENT_ACTIONS } from "../Actions/IncidentActions";
import { IncidentsContext } from "../Contexts/IncidentsContext";
import { UserContext } from "../Contexts/UserContext";
import { MutationContext } from "../Contexts/MutationContext";
import CustomAutoComplete from "./CustomAutoComplete";
//styling
import { useStyles } from "../Styles/styles";
import addButton from "../Assets/addButton.png";
import deleteButton from "../Assets/deleteButton.png";

//utils
import { shouldBeLocked } from "../utils/Roles";
import { sortByOriName } from "../utils/utils";
import { getValueFromEventValue } from "../utils/getValueFromEventValue";

const SuspectVictimAffiliation = ({
  formStyle,
  fabStyle,
  addOffenseVictimSuspect,
  removeOffenseVictimSuspect,
  svaLength,
  incidentPeople,
  svaIndex,
  sva,
  orisData,
  orisLoading,
  orisError,
  incidentId,
  incidentIndex,
  offenseId,
  offenseIndex,
  disabled = undefined,
  userRoles,
  ...props
}) => {
  const memoizedLocked = useMemo(
    () => shouldBeLocked(disabled, props?.canEdit, userRoles),
    [disabled, props?.canEdit, userRoles]
  );
  const classes = useStyles();
  const inputLabel = useRef(null);
  const [labelWidth, setLabelWidth] = useState(0);
  const { setup } = useContext(SetupContext);
  const { incidentDispatch } = useContext(IncidentsContext);
  const { UPDATE_SVA } = INCIDENT_ACTIONS;
  const { updateSvaInOffense } = useContext(MutationContext);
  const { user, onlineState } = useContext(UserContext);

  if (sva?.sealed && incidentPeople) {
    const sealedPerson = {
      id: sva?.suspect?.id,
      name: `${sva?.suspect?.personalAttribute?.lastName}`,
    };
    incidentPeople = incidentPeople.concat(sealedPerson);
  }

  useEffect(() => {
    if (inputLabel.current) {
      setLabelWidth(inputLabel.current.offsetWidth);
    }
  }, [inputLabel]);

  const [oris, setOris] = useState([]);

  const formatOris = data => {
    let arr = [];
    data.getOris.map(ori => {
      arr.push({ ...ori, name: ori.oriName });
      return null;
    });
    return arr;
  };

  useEffect(() => {
    if (orisData) {
      setOris(formatOris(orisData));
    }
  }, [orisData]);

  const {
    ibrSuspectVictimRelationshipTypes,
    biasTypes,
    victimLeoStates,
    victimLeoAssignments,
  } = setup.setupLists;
  const stateToMutation = {
    suspect: "suspectId",
    victim: "victimId",
    ibrSuspectVictimRelationshipType: "ibrSuspectVictimRelationshipTypeId",
    biasType: "biasTypeId",
    victimLeoState: "victimLeoStateId",
    victimLeoAssignment: "victimLeoAssignmentId",
    svaOri: "otherOriId",
    leokaNarrative: "leokaNarrative",
  };

  const handleChange = (event, submit = true) => {
    const name = event.target.name || null;
    const value = event.target.value;
    const mutationValue = getValueFromEventValue(event.target.value);
    const fieldToUpdate = stateToMutation[name];
    if (!name) {
      alert(
        `Please add a name field that matches the state name to the component that was just updated`
      );
      return null;
    }
    incidentDispatch({
      type: UPDATE_SVA,
      payload: {
        incidentId: incidentId,
        offenseId: offenseId,
        svaId: sva.id,
        property: name,
        value: value,
      },
    });
    submit &&
      updateSvaInOffense({
        variables: {
          input: {
            originatorId: user?.id,
            incidentOffenseId: offenseId,
            suspectVictimAffiliationId: sva.id,
            [fieldToUpdate]: mutationValue,
          },
        },
      });
  };

  const handleAutoSuggestChange = (value, name) => {
    const fieldToUpdate = stateToMutation[name];
    if (!name) {
      alert(
        `Please add a name field that matches the state name to the component that was just updated`
      );
      return null;
    }
    incidentDispatch({
      type: UPDATE_SVA,
      payload: {
        incidentId: incidentId,
        offenseId: offenseId,
        svaId: sva.id,
        property: name,
        value: value,
      },
    });
    updateSvaInOffense({
      variables: {
        input: {
          originatorId: user?.id,
          incidentOffenseId: offenseId,
          suspectVictimAffiliationId: sva.id,
          [fieldToUpdate]: value?.id ? value.id : value,
        },
      },
    });
  };

  const sortedPdOris = useMemo(() => {
    return sortByOriName(oris.filter(ori => ori?.agencyType === "PD"));
  }, [oris]);

  if (orisLoading && onlineState) return <CircularProgress />;
  if (orisError) return <div>Error: {orisError}</div>;
  if (
    orisData ||
    !onlineState ||
    import.meta.env.VITE_FORCE_ONLINE === "true"
  ) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          width: "100%",
          border: "1px solid grey",
        }}
      >
        <SingleDropDown
          handleChange={handleChange}
          stateName={"suspect"}
          disabled={sva?.sealed}
          value={
            incidentPeople?.length > 0
              ? incidentPeople.find(
                  person =>
                    person.id?.toString() === sva?.suspect?.id?.toString()
                )
              : ""
          }
          options={incidentPeople}
          title={"Offense Suspect"}
          classStyle={formStyle}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
        />
        <SingleDropDown
          handleChange={handleChange}
          stateName={"victim"}
          value={
            incidentPeople?.length > 0
              ? incidentPeople.find(
                  person =>
                    person?.id?.toString() === sva?.victim?.id?.toString()
                )
              : ""
          }
          options={incidentPeople}
          title={"Offense Victim"}
          classStyle={formStyle}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
        />
        <CustomAutoComplete
          title={"IBR Victim-Offender"}
          loading={false}
          options={ibrSuspectVictimRelationshipTypes}
          error={""}
          value={sva?.ibrSuspectVictimRelationshipType}
          stateName={"ibrSuspectVictimRelationshipType"}
          updateFields={handleAutoSuggestChange}
          userRoles={userRoles}
          {...props}
        />
        <CustomAutoComplete
          title={"Bias/Motivation (anti)"}
          loading={false}
          options={biasTypes}
          error={""}
          value={sva?.biasType}
          stateName={"biasType"}
          updateFields={handleAutoSuggestChange}
          userRoles={userRoles}
          {...props}
        />
        <SingleDropDown
          handleChange={handleChange}
          stateName={"victimLeoState"}
          value={sva?.victimLeoState}
          options={victimLeoStates}
          title={"V. was LEO"}
          classStyle={formStyle}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
        />
        <SingleDropDown
          handleChange={handleChange}
          stateName={"victimLeoAssignment"}
          value={sva?.victimLeoAssignment}
          options={victimLeoAssignments}
          title={"V. was LEO Assignment"}
          classStyle={formStyle}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
        />
        <SingleDropDown
          handleChange={handleChange}
          stateName={"svaOri"}
          value={sva?.svaOri}
          options={sortedPdOris}
          title={"Other ORI"}
          classStyle={formStyle}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
          name={"oriName"}
        />
        <TextField
          data-cy="leokaNarrative"
          inputProps={{ "data-cy": "leokaNarrative-input" }}
          InputLabelProps={{ shrink: true }}
          id="leokaNarrative-id"
          label="LEOKA Narrative"
          disabled={memoizedLocked}
          value={sva?.leokaNarrative || ""}
          name={"leokaNarrative"}
          onChange={e => handleChange(e, false)}
          onBlur={e => handleChange(e, true)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.target.blur();
            }
          }}
        />
        <FormControl
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
          className={formStyle}
        >
          {svaLength > 1 && (
            <button
              type="button"
              onClick={
                !memoizedLocked
                  ? () => removeOffenseVictimSuspect(sva.id, svaIndex)
                  : null
              }
              disabled={memoizedLocked}
              style={{ background: "none", border: 0, padding: 0 }}
            >
              <img
                data-cy={"delete-sva-button-" + sva?.id}
                src={deleteButton}
                className={classes.pngButton}
                alt="Delete SVA"
              />
            </button>
          )}
          {svaIndex === svaLength - 1 && (
            <button
              type="button"
              onClick={!memoizedLocked ? () => addOffenseVictimSuspect() : null}
              disabled={memoizedLocked}
              style={{ background: "none", border: 0, padding: 0 }}
            >
              <img
                data-cy={"add-sva-button-" + sva?.id}
                src={addButton}
                className={classes.pngButton}
                alt="Add SVA"
              />
            </button>
          )}
        </FormControl>
      </div>
    );
  }
};

export default SuspectVictimAffiliation;
